import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
  Fingerprint,
} from 'lucide-react';
import { cn } from '@lib/utils/cn';
import { useUIStore } from '@stores/ui';
import { usePermission } from '@hooks/usePermission';
import { useAuthStore } from '@stores/auth';
import { userProfileApi } from '@features/settings/api/userProfileApi';
import { prefetchRoute } from '@lib/api/prefetchMap';
import BuildStamp from '@components/BuildStamp';
import {
  NAV_ITEMS,
  FOOTER_ITEMS as FOOTER_CONFIG,
  TONE,
  type NavLeafConfig,
} from '@lib/rbac/navConfig';

type LucideIcon = React.ComponentType<{ className?: string }>;

interface NavLeaf {
  /**
   * Stable nav-section id (mirrors `NavLeafConfig.id`). Used both as a React
   * key source and to match against the per-user `hiddenMenuSections` layer.
   */
  id: string;
  to: string;
  labelKey: string;
  icon: LucideIcon;
  /**
   * Permission gate. Accepts a single key OR an array of keys (matched in
   * "any" mode — the leaf shows if the user has *any* of the listed keys).
   * Hub entries use arrays so the menu still surfaces when an operator has
   * granted only a subset of the hub's tabs.
   *
   * Ignored when `alwaysVisible` is true.
   */
  permission?: string | string[];
  /**
   * When true, the leaf bypasses every permission check. Used for the four
   * base hubs that must always surface regardless of the user's permission
   * map (Attendance, Rules, Billing, System health).
   */
  alwaysVisible?: boolean;
  /** Optional explicit hex tone for the leaf icon. Falls back to the group tone. */
  tone?: string;
}

interface NavGroup {
  id: string;
  labelKey: string;
  icon: LucideIcon;
  tone: string;
  children: NavLeaf[];
}

type NavSection =
  | { kind: 'leaf'; item: NavLeaf }
  | { kind: 'group'; group: NavGroup };

/* ──────────────────────────────────────────────────────────────────────────
   The TONE palette and the nav entries themselves now live in the central
   `web/src/lib/rbac/navConfig.ts` so route guards (`<ProtectedRoute>`) and
   the sidebar share a single source of truth. This adapter layer converts
   the config into the local `NavSection` shape the existing rendering code
   expects (leaves vs. groups). All entries are leaves today — `NavGroup` is
   kept around in case we want collapsible groups again later.
   ────────────────────────────────────────────────────────────────────── */

function leafFromConfig(cfg: NavLeafConfig): NavLeaf {
  return {
    id: cfg.id,
    to: cfg.to,
    labelKey: cfg.labelKey,
    icon: cfg.icon,
    tone: cfg.tone,
    alwaysVisible: cfg.alwaysVisible,
    // Collapse single-key permission arrays so existing call sites keep
    // working with `string | string[]`.
    permission: cfg.alwaysVisible
      ? undefined
      : cfg.permissions && cfg.permissions.length === 1
        ? cfg.permissions[0]
        : cfg.permissions,
  };
}

const NAV: NavSection[] = NAV_ITEMS.map((cfg) => ({
  kind: 'leaf' as const,
  item: leafFromConfig(cfg),
}));

const FOOTER_ITEMS: NavLeaf[] = FOOTER_CONFIG.map(leafFromConfig);

/**
 * Fallback labels so the sidebar is readable even before translations load.
 * Bilingual on purpose: the i18next catalog is database-backed, so a missing /
 * empty catalog value must never leak Arabic into the English UI.
 */
const DEFAULT_LABELS: Record<string, { ar: string; en: string }> = {
  // Groups
  'nav.group.students': { ar: 'الطلاب', en: 'Students' },
  'nav.group.devices': { ar: 'إدارة الأجهزة', en: 'Devices Management' },
  'nav.search.placeholder': { ar: 'بحث في القائمة...', en: 'Search the menu...' },
  'nav.search.empty': { ar: 'لا توجد نتائج', en: 'No results' },

  // Overview
  'nav.dashboard': { ar: 'لوحة التحكم', en: 'Dashboard' },
  'nav.quickAccess': { ar: 'الوصول السريع', en: 'Quick access' },

  // Staff hub (employees, admin users, visitors, tags, records, schedule
  // history, and bulk imports — all inside one tabbed page).
  'nav.staff': { ar: 'إدارة الموظفين', en: 'Staff Management' },

  // Students
  'nav.studentsHub': { ar: 'الطلاب', en: 'Students' },

  // Devices
  'nav.devicesHub': { ar: 'إدارة الأجهزة', en: 'Devices Management' },

  // Attendance management hub (leaves, leave types, time-based leaves,
  // extra minutes, rewards & penalties — all inside one tabbed page).
  'nav.attendanceMgmt': { ar: 'إدارة الدوام', en: 'Attendance Management' },

  // Shifts & Time hub (attendance log, shifts, time slots, overtime slots —
  // all inside one tabbed page).
  'nav.workTime': { ar: 'إدارة الحضور', en: 'Shifts & Time Management' },

  // Single-entry hubs (tabs are inside the page)
  'nav.rules': { ar: 'القواعد', en: 'Rules' },
  'nav.actionPolicies': { ar: 'إجراءات وشروط', en: 'Actions & Conditions' },
  'nav.reports': { ar: 'التقارير', en: 'Reports' },
  'nav.settings': { ar: 'الإعدادات', en: 'Settings' },
  'nav.recruitment': { ar: 'التعيين', en: 'Recruitment' },
  'nav.supervisorRequests': { ar: 'طلبات المشرفين', en: 'Supervisor requests' },
  'nav.complaints': { ar: 'شكاوى الموظفين', en: 'Employee Complaints' },
  'nav.evaluation': { ar: 'تقييم الموظفين', en: 'Employee Evaluation' },
  'nav.surveys': { ar: 'استبيانات الموظفين', en: 'Employee surveys' },
  'nav.visitors': { ar: 'الزوار', en: 'Visitors' },

  // Notifications system
  'nav.notifications': { ar: 'الإشعارات', en: 'Notifications' },
  'nav.broadcast': { ar: 'إرسال تبليغ', en: 'Broadcast' },
  'nav.notificationTemplates': { ar: 'قوالب التبليغات', en: 'Notification templates' },

  // Billing / Health
  'nav.billing': { ar: 'الفواتير', en: 'Billing' },
  'nav.health': { ar: 'حالة النظام', en: 'System health' },
};

function defaultLabel(key: string, isRtl: boolean): string {
  const pair = DEFAULT_LABELS[key];
  if (!pair) return key;
  return isRtl ? pair.ar : pair.en;
}

/* ──────────────────────────────────────────────────────────────────────────
   Style helpers — opacity-suffixed hex (e.g. `#6366f11A`) lets us use the
   same tone colour at multiple intensities (tile bg, hover bg, indicator).
   ────────────────────────────────────────────────────────────────────── */
const tile = (tone: string, active = false) => ({
  backgroundColor: active ? `${tone}33` /* ~20% */ : `${tone}1F` /* ~12% */,
  color: tone,
});
const activeRow = (tone: string) => ({
  backgroundColor: `${tone}14`, // ~8%
});

interface LeafProps {
  item: NavLeaf;
  collapsed: boolean;
  /** When true, the item is rendered as a child of a group (indented). */
  nested?: boolean;
  /** Group tone used as fallback when leaf has no explicit tone. */
  fallbackTone?: string;
}

function NavLeafItem({ item, collapsed, nested, fallbackTone }: LeafProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  // Always-visible leaves bypass the permission check entirely. Used for the
  // four base hubs (attendance, rules, billing, health) that must surface for
  // every authenticated admin user regardless of granted permissions.
  const permAllowed = usePermission(item.permission);
  const allowed = item.alwaysVisible ? true : permAllowed;
  // Per-user "side-menu visibility" layer — INDEPENDENT of permissions. When
  // the leaf's id is listed in `hiddenMenuSections`, hide it even if the user
  // is permitted (and even for `alwaysVisible` hubs like Attendance / Rules).
  const hiddenMenuSections = useAuthStore((s) => s.hiddenMenuSections);
  const hidden = hiddenMenuSections.includes(item.id);
  if (!allowed || hidden) return null;
  const Icon = item.icon;
  const onWarm = () => prefetchRoute(item.to);
  const label = t(item.labelKey, defaultLabel(item.labelKey, isRtl));
  const tone = item.tone ?? fallbackTone ?? TONE.slate;

  return (
    <NavLink
      to={item.to}
      onMouseEnter={onWarm}
      onFocus={onWarm}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          'group/leaf relative flex items-center gap-2.5 rounded-lg text-sm transition-all duration-150',
          'hover:bg-bg-muted/70',
          collapsed ? 'justify-center p-2' : 'px-2 py-1.5',
          nested && !collapsed && 'ps-2',
          isActive ? 'font-semibold text-fg' : 'font-medium text-fg/85',
        )
      }
      style={({ isActive }) => (isActive ? activeRow(tone) : undefined)}
    >
      {({ isActive }) => (
        <>
          {isActive && !collapsed && (
            <span
              aria-hidden
              className="absolute start-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-full"
              style={{ backgroundColor: tone }}
            />
          )}
          <span
            aria-hidden
            className={cn(
              'grid place-items-center rounded-md shrink-0 transition-all',
              nested && !collapsed ? 'h-6 w-6' : 'h-7 w-7',
            )}
            style={tile(tone, isActive)}
          >
            <Icon className={cn(nested && !collapsed ? 'h-3.5 w-3.5' : 'h-4 w-4')} />
          </span>
          {!collapsed && (
            <span className={cn('truncate flex-1', nested && 'text-[13px]')}>{label}</span>
          )}
        </>
      )}
    </NavLink>
  );
}

interface GroupProps {
  group: NavGroup;
  collapsed: boolean;
  forceOpen?: boolean;
}

function NavGroupItem({ group, collapsed, forceOpen }: GroupProps) {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const isRtl = i18n.dir() === 'rtl';
  const open = useUIStore((s) => s.sidebarOpenGroups.includes(group.id));
  const setOpenGroups = useUIStore((s) => s.setSidebarOpenGroups);
  const setCollapsed = useUIStore((s) => s.setSidebarCollapsed);

  if (group.children.length === 0) return null;

  const Icon = group.icon;
  const tone = group.tone;
  const isActive = group.children.some((c) => pathname === c.to || pathname.startsWith(`${c.to}/`));
  const isOpen = forceOpen || open || isActive;
  const label = t(group.labelKey, defaultLabel(group.labelKey, isRtl));

  // Accordion behavior: only one group open at a time. Clicking the open group
  // collapses it; clicking any other group replaces the open one.
  const handleToggle = () => {
    if (collapsed) {
      setCollapsed(false);
      setOpenGroups([group.id]);
      return;
    }
    setOpenGroups(open ? [] : [group.id]);
  };

  const Chevron = isOpen ? ChevronDown : isRtl ? ChevronLeft : ChevronRight;

  return (
    <HasAnyAllowedChild items={group.children}>
      <div className="relative">
        <button
          type="button"
          onClick={handleToggle}
          aria-expanded={isOpen}
          aria-controls={`sidebar-group-${group.id}`}
          title={collapsed ? label : undefined}
          className={cn(
            'group/group relative flex w-full items-center gap-2.5 rounded-lg text-sm font-semibold transition-all duration-150',
            'hover:bg-bg-muted/70',
            collapsed ? 'justify-center p-2' : 'px-2 py-2',
            isActive ? 'text-fg' : 'text-fg/90',
          )}
          style={isActive ? activeRow(tone) : undefined}
        >
          {isActive && !collapsed && (
            <span
              aria-hidden
              className="absolute start-0 top-1.5 bottom-1.5 w-[3px] rounded-full"
              style={{ backgroundColor: tone }}
            />
          )}
          <span
            aria-hidden
            className="grid place-items-center h-7 w-7 rounded-md shrink-0 transition-all"
            style={tile(tone, isActive)}
          >
            <Icon className="h-4 w-4" />
          </span>
          {!collapsed && (
            <>
              <span className="truncate flex-1 text-start">{label}</span>
              <Chevron className="ms-auto h-3.5 w-3.5 text-fg-subtle transition-transform duration-200" />
            </>
          )}
        </button>

        {!collapsed && isOpen && (
          <div
            id={`sidebar-group-${group.id}`}
            className="mt-1 mb-1 space-y-0.5 ms-4 ps-2 border-s border-border/50"
          >
            {group.children.map((child) => (
              <NavLeafItem
                key={`${group.id}-${child.to}`}
                item={child}
                collapsed={false}
                nested
                fallbackTone={tone}
              />
            ))}
          </div>
        )}
      </div>
    </HasAnyAllowedChild>
  );
}

/**
 * Wrapper that renders children only if the user has at least one allowed item in the group.
 * This prevents showing an empty group header when permissions strip out every child.
 */
function HasAnyAllowedChild({
  items,
  children,
}: {
  items: NavLeaf[];
  children: React.ReactNode;
}) {
  const permissions = useAuthStore((s) => s.permissions);
  const allowedSet = Array.isArray(permissions) ? new Set(permissions) : new Set<string>();
  const hasAny = items.some((it) => {
    if (it.alwaysVisible) return true;
    if (!it.permission) return true;
    if (Array.isArray(it.permission)) return it.permission.some((k) => allowedSet.has(k));
    return allowedSet.has(it.permission);
  });
  if (!hasAny) return null;
  return <>{children}</>;
}

function SidebarSearch({
  value,
  onChange,
  collapsed,
}: {
  value: string;
  onChange: (v: string) => void;
  collapsed: boolean;
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  if (collapsed) return null;
  return (
    <div className="relative px-3 pt-3 pb-2">
      <Search className="pointer-events-none absolute start-5 top-1/2 -translate-y-1/2 h-4 w-4 text-fg-muted" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('nav.search.placeholder', defaultLabel('nav.search.placeholder', isRtl))}
        className="w-full rounded-lg border border-border bg-bg-muted/40 ps-9 pe-8 py-2 text-sm placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition-colors"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="clear"
          className="absolute end-5 top-1/2 -translate-y-1/2 rounded p-0.5 text-fg-muted hover:text-fg hover:bg-bg-muted"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

interface SearchableLeaf {
  item: NavLeaf;
  groupTone?: string;
  groupLabel?: string;
}

function flattenForSearch(): SearchableLeaf[] {
  const out: SearchableLeaf[] = [];
  for (const section of NAV) {
    if (section.kind === 'leaf') {
      out.push({ item: section.item });
    } else if (section.kind === 'group') {
      for (const child of section.group.children) {
        out.push({
          item: child,
          groupTone: section.group.tone,
          groupLabel: section.group.labelKey,
        });
      }
    }
  }
  for (const f of FOOTER_ITEMS) out.push({ item: f });
  return out;
}

function SearchResults({
  query,
  collapsed,
}: {
  query: string;
  collapsed: boolean;
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const all = useMemo(() => flattenForSearch(), []);
  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return [] as SearchableLeaf[];
    return all.filter(({ item }) => {
      const label = defaultLabel(item.labelKey, isRtl).toLowerCase();
      const translated = t(item.labelKey, defaultLabel(item.labelKey, isRtl))
        .toString()
        .toLowerCase();
      return label.includes(q) || translated.includes(q) || item.to.toLowerCase().includes(q);
    });
  }, [all, q, t, isRtl]);

  if (matches.length === 0) {
    return (
      <div className="px-3 py-6 text-center text-sm text-fg-muted">
        {t('nav.search.empty', defaultLabel('nav.search.empty', isRtl))}
      </div>
    );
  }

  return (
    <div className="space-y-0.5 px-3">
      {matches.map(({ item, groupTone, groupLabel }) => (
        <div key={`${groupLabel ?? 'top'}-${item.to}`} className="flex flex-col">
          {groupLabel && (
            <span className="px-3 pt-1 text-[10px] uppercase tracking-wide text-fg-muted">
              {t(groupLabel, defaultLabel(groupLabel, isRtl))}
            </span>
          )}
          <NavLeafItem item={item} collapsed={collapsed} fallbackTone={groupTone} />
        </div>
      ))}
    </div>
  );
}

function SidebarFooter({ collapsed }: { collapsed: boolean }) {
  return (
    <div className="border-t border-border px-3 py-3 space-y-1">
      {FOOTER_ITEMS.map((item) => (
        <NavLeafItem key={item.to} item={item} collapsed={collapsed} />
      ))}
      {!collapsed && (
        <div className="pt-1.5 text-center">
          <BuildStamp />
        </div>
      )}
    </div>
  );
}

/**
 * Ambient animated background for the sidebar — drifting colour blobs,
 * a faint dotted grid, and a slow vertical "scan" line that evokes a
 * fingerprint / live-attendance heartbeat. All purely decorative: it sits
 * behind the menu (negative z-index inside the parent's isolated stacking
 * context), is `pointer-events-none`, and pauses for users with
 * `prefers-reduced-motion`. Theme colours come from CSS vars so it adapts
 * to light/dark automatically.
 */
function SidebarBackgroundFx() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
    >
      {/* Top brand wash */}
      <div
        className="absolute inset-x-0 top-0 h-32"
        style={{
          background:
            'linear-gradient(to bottom, rgb(var(--brand) / 0.10), transparent)',
        }}
      />

      {/* Drifting colour blobs — team spirit / warm ambient */}
      <div
        className="sidebar-fx-blob-a absolute -top-10 -end-12 h-56 w-56 rounded-full blur-3xl"
        style={{
          background:
            'radial-gradient(circle, rgb(var(--brand) / 0.45) 0%, transparent 70%)',
          opacity: 0.35,
        }}
      />
      <div
        className="sidebar-fx-blob-b absolute top-1/3 -start-16 h-64 w-64 rounded-full blur-3xl"
        style={{
          background:
            'radial-gradient(circle, rgb(var(--success) / 0.35) 0%, transparent 70%)',
          opacity: 0.28,
        }}
      />
      <div
        className="sidebar-fx-blob-c absolute bottom-12 -end-16 h-60 w-60 rounded-full blur-3xl"
        style={{
          background:
            'radial-gradient(circle, rgb(var(--accent) / 0.40) 0%, transparent 70%)',
          opacity: 0.26,
        }}
      />

      {/* Faint dotted grid — precision / structured HR */}
      <div
        className="absolute inset-0 opacity-[0.06] dark:opacity-[0.08]"
        style={{
          backgroundImage:
            'radial-gradient(rgb(var(--fg) / 0.6) 1px, transparent 1px)',
          backgroundSize: '18px 18px',
        }}
      />

      {/* Vertical scan line — fingerprint / live attendance pulse */}
      <div
        className="sidebar-fx-scan absolute inset-x-0 h-[2px]"
        style={{
          background:
            'linear-gradient(to right, transparent 0%, rgb(var(--brand) / 0.65) 50%, transparent 100%)',
          boxShadow: '0 0 14px 1px rgb(var(--brand) / 0.45)',
        }}
      />
    </div>
  );
}

/**
 * Brand block at the top of the sidebar — shows the **client's company
 * name** (so each tenant feels they own the system) plus a HR-themed
 * Fingerprint icon. Falls back to a friendly default while the profile is
 * still loading or if no company name is configured yet.
 */
function SidebarBrand({ collapsed, isRtl }: { collapsed: boolean; isRtl: boolean }) {
  const isAuth = useAuthStore((s) => s.isAuthenticated);
  const { data: profile } = useQuery({
    queryKey: ['users', 'profile'],
    queryFn: userProfileApi.get,
    staleTime: 5 * 60_000,
    enabled: isAuth,
  });
  const companyName = profile?.companyName?.trim();
  const companySlogan = profile?.companySlogan?.trim();
  const companyLogo = profile?.companyLogo;

  const displayName = companyName || (isRtl ? 'نظام الحضور' : 'Attendance System');
  const displaySlogan =
    companySlogan || (isRtl ? 'نظام الحضور والانصراف' : 'Time & Attendance');

  return (
    <div className="flex h-16 items-center gap-3 border-b border-border px-4 shrink-0 relative">
      {/* Logo / icon — uses uploaded company logo when available, otherwise
          a Fingerprint icon (the most universal "attendance" symbol). */}
      <div
        className="relative h-9 w-9 shrink-0 rounded-xl text-brand-foreground grid place-items-center font-bold shadow-md overflow-hidden"
        style={{
          background:
            'linear-gradient(135deg, rgb(var(--brand)) 0%, rgb(var(--brand-hover)) 100%)',
        }}
      >
        {companyLogo ? (
          <img
            src={companyLogo}
            alt={displayName}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <Fingerprint className="h-[1.15rem] w-[1.15rem]" strokeWidth={2.25} />
        )}
        {/* Live indicator — pulses to signal the attendance system is active. */}
        <span aria-hidden className="absolute -top-0.5 -end-0.5 grid place-items-center">
          <span
            className="absolute inline-flex h-2.5 w-2.5 rounded-full sidebar-fx-pulse"
            style={{ backgroundColor: 'rgb(var(--success))' }}
          />
          <span
            className="relative inline-flex h-2 w-2 rounded-full ring-2 ring-surface"
            style={{ backgroundColor: 'rgb(var(--success))' }}
          />
        </span>
      </div>
      {!collapsed && (
        <div className="min-w-0">
          <div className="text-sm font-bold truncate leading-tight text-fg" title={displayName}>
            {displayName}
          </div>
          <div className="text-[11px] text-fg-muted leading-tight mt-0.5 truncate" title={displaySlogan}>
            {displaySlogan}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Tiny live-status strip just under the brand. Communicates "the system is
 * online and tracking right now" with a pulsing green dot and the live time
 * — adds a fragment of real-time energy to the sidebar without being noisy.
 */
function SidebarLiveStrip({ isRtl }: { isRtl: boolean }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  // Always render the live clock with English (Latin) digits even in RTL —
  // the strip uses tabular-nums for steady width and product-wide convention
  // is to keep on-screen times in `en-US` so HH:MM never flips between locales.
  const time = now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return (
    <div className="px-4 py-2 border-b border-border/60 flex items-center justify-between gap-2 text-[11px]">
      <div className="inline-flex items-center gap-1.5 text-fg-muted">
        <span className="relative inline-flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full rounded-full opacity-75 sidebar-fx-pulse"
                style={{ backgroundColor: 'rgb(var(--success))' }} />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: 'rgb(var(--success))' }} />
        </span>
        <span className="font-semibold text-success">
          {isRtl ? 'النظام نشط' : 'Live'}
        </span>
      </div>
      <span className="font-mono tabular-nums text-fg-muted">{time}</span>
    </div>
  );
}

export function Sidebar() {
  const { i18n } = useTranslation();
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const setOpenGroups = useUIStore((s) => s.setSidebarOpenGroups);
  const { pathname } = useLocation();
  const [query, setQuery] = useState('');

  // Accordion: keep ONLY the group that contains the current route open.
  // All other groups stay closed for a cleaner look.
  useEffect(() => {
    let activeId: string | null = null;
    for (const section of NAV) {
      if (section.kind !== 'group') continue;
      const match = section.group.children.some(
        (c) => pathname === c.to || pathname.startsWith(`${c.to}/`),
      );
      if (match) {
        activeId = section.group.id;
        break;
      }
    }
    setOpenGroups(activeId ? [activeId] : []);
  }, [pathname, setOpenGroups]);

  const isSearching = query.trim().length > 0;

  return (
    <aside
      className={cn(
        'sticky top-0 h-screen shrink-0 border-e border-border bg-surface transition-[width] duration-200 flex flex-col relative isolate overflow-hidden',
        collapsed ? 'w-[72px]' : 'w-72',
      )}
    >
      <SidebarBackgroundFx />
      <SidebarBrand collapsed={collapsed} isRtl={i18n.dir() === 'rtl'} />
      {/* live status strip — only visible expanded — short reassurance that
          the attendance system is online and tracking. */}
      {!collapsed && <SidebarLiveStrip isRtl={i18n.dir() === 'rtl'} />}

      {/* Search */}
      <SidebarSearch value={query} onChange={setQuery} collapsed={collapsed} />

      {/* Nav body */}
      <nav className="flex-1 overflow-y-auto px-3 pb-3 space-y-0.5">
        {isSearching ? (
          <SearchResults query={query} collapsed={collapsed} />
        ) : (
          <>
            {NAV.map((section) => {
              if (section.kind === 'leaf') {
                return (
                  <NavLeafItem
                    key={section.item.to}
                    item={section.item}
                    collapsed={collapsed}
                  />
                );
              }
              return (
                <NavGroupItem
                  key={section.group.id}
                  group={section.group}
                  collapsed={collapsed}
                />
              );
            })}
          </>
        )}
      </nav>

      {/* Footer (Billing, Health) */}
      <SidebarFooter collapsed={collapsed} />
    </aside>
  );
}
